import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { CircleStop, Mic, Plus, Trash2, Video, X } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { api } from "@/lib/api";
import { ISSUANCE_DEFAULT_EXPIRY_DAYS, VIEWER_ROLES } from "@demore/shared/constants";
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/lib/auth";

type Recipient = { name: string; email: string; company: string };

// Display names for the audience-card segments (matches the server-side card
// labels in admin/audience.ts).
const SEGMENT_LABELS: Record<string, string> = {
  lead: "Leads",
  existing_client: "Existing Clients",
  campaign_ceo: "Campaign CEO",
  campaign_team: "Campaign Team",
  investor: "Investors",
  specific_member: "Specific Members",
};

export function ComposeIssuancePage() {
  const nav = useNavigate();
  // CEO Rev 1.0 Phase 2: the Audience card's "Send Playlist" deep-links here as
  // ?campaignId=…&segment=…. Pre-select the campaign (which autofills its
  // playlist + welcome below) and surface the target segment as context.
  const [searchParams] = useSearchParams();
  const prefillCampaignId = searchParams.get("campaignId") ?? "";
  const prefillSegment = searchParams.get("segment") ?? "";
  const [playlistId, setPlaylistId] = useState("");
  const [recipients, setRecipients] = useState<Recipient[]>([{ name: "", email: "", company: "" }]);

  // Decision 2A: the Audience card deep-link also pre-fills the recipient list
  // with the campaign's members in that segment — fully editable before send.
  // Applies once, and only while the list is still the single untouched blank
  // row, so it can never clobber anything the AE already typed.
  const { data: segmentRecipients } = useQuery({
    queryKey: ["audience-recipients", prefillCampaignId, prefillSegment],
    queryFn: () =>
      api.get<{ items: Array<{ name: string; email: string }> }>(
        `/api/admin/campaigns/${prefillCampaignId}/audience/recipients?segment=${encodeURIComponent(prefillSegment)}`,
      ),
    enabled: !!prefillCampaignId && !!prefillSegment,
  });
  const recipientsPrefilledKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const prefillKey = `${prefillCampaignId}:${prefillSegment}`;
    if (recipientsPrefilledKeyRef.current === prefillKey) return;
    const items = segmentRecipients?.items ?? [];
    if (!items.length) return;
    setRecipients((rs) => {
      const first = rs[0];
      const untouched =
        rs.length === 1 && !!first && !first.name && !first.email && !first.company;
      if (!untouched) return rs;
      recipientsPrefilledKeyRef.current = prefillKey;
      return items.map((m) => ({ name: m.name ?? "", email: m.email ?? "", company: "" }));
    });
  }, [prefillCampaignId, prefillSegment, segmentRecipients?.items]);

  // #9.4: auto-fill a recipient's name/company from ElevateCRM when the AE tabs
  // out of the email field. Best-effort — silently no-ops if CRM read is off or
  // there's no match, and never overwrites a value the AE already typed.
  async function lookupContact(idx: number, email: string) {
    if (!email.trim()) return;
    try {
      const r = await api.get<{ found: boolean; name?: string; company?: string }>(
        `/api/admin/crm/contacts?email=${encodeURIComponent(email)}`,
      );
      if (!r.found) return;
      setRecipients((rs) =>
        rs.map((x, i) =>
          i === idx
            ? { ...x, name: x.name || r.name || "", company: x.company || r.company || "" }
            : x,
        ),
      );
    } catch {
      // optional enhancement — ignore failures
    }
  }
  const [welcomeMessage, setWelcomeMessage] = useState("");
  const [ctaLabel, setCtaLabel] = useState("Book a follow-up call");
  const [ctaUrl, setCtaUrl] = useState("");
  const [ndaRequired, setNdaRequired] = useState(false);
  const [allowForwarding, setAllowForwarding] = useState(true);
  const [accountRef, setAccountRef] = useState("");
  const [dealRef, setDealRef] = useState("");
  const [noViewCadenceDays, setNoViewCadenceDays] = useState<number | "">(3);
  // Audit V1
  const [recipientRole, setRecipientRole] = useState<string>("");
  const [suppressAnalyticsInEmail, setSuppressAnalyticsInEmail] = useState(true);
  const [sentUrls, setSentUrls] = useState<string[] | null>(null);

  const defaultExpiry = useMemo(() => {
    const d = new Date(Date.now() + ISSUANCE_DEFAULT_EXPIRY_DAYS * 86400_000);
    return d.toISOString().slice(0, 16);
  }, []);
  const [expiry, setExpiry] = useState(defaultExpiry);

  const { data: playlists } = useQuery({
    queryKey: ["playlists-compose"],
    queryFn: () => api.get<{ items: Array<{ id: string; name: string; roles?: string[] }> }>("/api/admin/playlists"),
  });
  // Detail of the chosen playlist — used to detect NDA-tier videos, which
  // force NDA on (otherwise they'd be permanently locked for the recipient).
  const { data: selectedPlaylist } = useQuery({
    queryKey: ["playlist-detail-compose", playlistId],
    queryFn: () =>
      api.get<{ items: Array<{ accessTier: string }> }>(`/api/admin/playlists/${playlistId}`),
    enabled: !!playlistId,
  });
  const hasNdaTierVideo = (selectedPlaylist?.items ?? []).some(
    (it) => it.accessTier === "nda_signed",
  );
  const { data: users } = useQuery({
    queryKey: ["users-compose"],
    queryFn: () => api.get<{ items: Array<{ id: string; name: string }> }>("/api/admin/users"),
  });
  // Audit V1: campaign picker drives defaults — selecting a campaign autofills
  // playlist + welcome message from that campaign's defaults (unless already
  // populated by the user).
  const [campaignId, setCampaignId] = useState(prefillCampaignId);
  const { data: campaigns } = useQuery({
    queryKey: ["campaigns-compose"],
    queryFn: () => api.get<{ items: Array<{ id: string; name: string; consentTier: string; persona?: string; defaultPlaylistId?: string | null; defaultWelcomeMessage?: string | null }> }>("/api/admin/campaigns"),
  });
  // Board #2511: "Named with NDA" campaigns must send NDA-gated packs.
  const selectedCampaign = campaigns?.items.find((c) => c.id === campaignId);
  const campaignRequiresNda = selectedCampaign?.consentTier === "named_with_nda";
  const [notifierUserIds, setNotifierUserIds] = useState<string[]>([]);
  const [personalIntroVideoId, setPersonalIntroVideoId] = useState<string | null>(null);
  const [introPreviewUrl, setIntroPreviewUrl] = useState<string | null>(null);

  // Audit V1: when a campaign is picked, apply its defaults to fields the
  // user hasn't explicitly touched. Tracks whether playlist/welcome were
  // ever auto-applied so a campaign re-select doesn't fight user edits.
  const autoAppliedRef = useRef<{ playlist?: string; welcome?: string }>({});
  useEffect(() => {
    if (!campaignId) return;
    const c = campaigns?.items.find((x) => x.id === campaignId);
    if (!c) return;
    if (c.defaultPlaylistId && (!playlistId || playlistId === autoAppliedRef.current.playlist)) {
      setPlaylistId(c.defaultPlaylistId);
      autoAppliedRef.current.playlist = c.defaultPlaylistId;
    }
    if (c.defaultWelcomeMessage && (!welcomeMessage || welcomeMessage === autoAppliedRef.current.welcome)) {
      setWelcomeMessage(c.defaultWelcomeMessage);
      autoAppliedRef.current.welcome = c.defaultWelcomeMessage;
    }
    if (c.consentTier === "named_with_nda") {
      setNdaRequired(true);
    }
  }, [campaignId, campaigns?.items]); // eslint-disable-line react-hooks/exhaustive-deps

  // Audit V1: when a recipient_role is set, surface playlists tagged with the
  // role in a "Suggested" group at the top of the picker.
  const playlistGroups = useMemo(() => {
    const all = playlists?.items ?? [];
    if (!recipientRole) return { suggested: [], rest: all };
    const suggested = all.filter((p) => (p.roles ?? []).includes(recipientRole));
    const suggestedIds = new Set(suggested.map((p) => p.id));
    return { suggested, rest: all.filter((p) => !suggestedIds.has(p.id)) };
  }, [playlists?.items, recipientRole]);

  // NDA-tier videos require NDA to be playable — auto-enable so the sender
  // can't accidentally ship a pack with permanently-locked videos.
  useEffect(() => {
    if (hasNdaTierVideo) setNdaRequired(true);
  }, [hasNdaTierVideo]);

  const send = useMutation({
    mutationFn: () =>
      api.post<{ issuanceIds: string[]; viewerUrls: string[] }>("/api/admin/issuances", {
        campaignId: campaignId || undefined,
        playlistId,
        recipients: recipients.map((r) => ({ name: r.name, email: r.email, company: r.company || undefined })),
        welcomeMessage: welcomeMessage || undefined,
        ctaLabel: ctaLabel || undefined,
        ctaUrl: ctaUrl || undefined,
        expiryDate: new Date(expiry).toISOString(),
        ndaRequired,
        allowForwarding,
        notifierUserIds,
        personalIntroVideoId: personalIntroVideoId ?? undefined,
        noViewCadenceDays: noViewCadenceDays === "" ? null : noViewCadenceDays,
        accountRef: accountRef || undefined,
        dealRef: dealRef || undefined,
        // Audit V1
        recipientRole: recipientRole || undefined,
        suppressAnalyticsInEmail,
      }),
    onSuccess: (data) => {
      toast.success(`Sent ${data.issuanceIds.length} pack(s)`);
      // Audit V1: show the copy-link modal instead of just navigating.
      setSentUrls(data.viewerUrls ?? []);
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Send failed"),
  });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 max-w-7xl">
      <div className="lg:col-span-2 space-y-4">
        <h1 className="font-head text-2xl font-semibold">Compose Demo Pack</h1>

        {/* Audit V1: optional campaign picker -- selecting one autofills
            playlist + welcome message from the campaign defaults. */}
        <Card>
          <CardHeader><CardTitle>Campaign (optional)</CardTitle></CardHeader>
          <CardContent>
            <Select value={campaignId} onChange={(e) => setCampaignId(e.target.value)}>
              <option value="">— skip; compose freely —</option>
              {campaigns?.items.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                  {c.persona ? ` (${c.persona.replace(/_/g, " ")})` : ""}
                </option>
              ))}
            </Select>
            {campaignId && (
              <p className="text-xs text-muted-foreground mt-2">
                Playlist + welcome message auto-fill from this campaign's
                defaults. You can still edit them below.
              </p>
            )}
            {prefillSegment && (
              <p className="text-xs text-gold bg-gold/10 border border-gold/30 rounded-md px-3 py-2 mt-2">
                Composing for this campaign's{" "}
                <strong>{SEGMENT_LABELS[prefillSegment] ?? prefillSegment.replace(/_/g, " ")}</strong>{" "}
                audience.{" "}
                {prefillSegment === "campaign_ceo"
                  ? "The campaign owner is always part of the Campaign CEO audience, so they are pre-filled below — adjust the recipients before sending."
                  : "Members of this audience are pre-filled below — adjust the recipients before sending."}
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Playlist</CardTitle></CardHeader>
          <CardContent>
            <Select value={playlistId} onChange={(e) => setPlaylistId(e.target.value)}>
              <option value="">Choose a playlist</option>
              {playlistGroups.suggested.length > 0 && (
                <optgroup label={`Suggested for ${recipientRole}`}>
                  {playlistGroups.suggested.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </optgroup>
              )}
              {playlistGroups.suggested.length > 0 ? (
                <optgroup label="All playlists">
                  {playlistGroups.rest.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </optgroup>
              ) : (
                playlistGroups.rest.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))
              )}
            </Select>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Recipients ({recipients.length})</CardTitle>
              <Button size="sm" variant="outline" onClick={() => setRecipients((r) => [...r, { name: "", email: "", company: "" }])}>
                <Plus className="h-4 w-4" />Add
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {recipients.map((r, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-4 space-y-1.5"><Label>Name</Label><Input value={r.name} onChange={(e) => setRecipients((rs) => rs.map((x, i) => i === idx ? { ...x, name: e.target.value } : x))} /></div>
                <div className="col-span-4 space-y-1.5"><Label>Email</Label><Input type="email" value={r.email} onChange={(e) => setRecipients((rs) => rs.map((x, i) => i === idx ? { ...x, email: e.target.value } : x))} onBlur={(e) => lookupContact(idx, e.target.value)} /></div>
                <div className="col-span-3 space-y-1.5"><Label>Company</Label><Input value={r.company} onChange={(e) => setRecipients((rs) => rs.map((x, i) => i === idx ? { ...x, company: e.target.value } : x))} /></div>
                <div className="col-span-1">
                  <Button size="icon" variant="ghost" onClick={() => setRecipients((rs) => rs.filter((_, i) => i !== idx))} disabled={recipients.length === 1}><X className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Personalization</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1.5">
              <Label>Welcome message</Label>
              <Textarea value={welcomeMessage} onChange={(e) => setWelcomeMessage(e.target.value)} placeholder="A short personal note — appears on the cover page" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>CTA label</Label><Input value={ctaLabel} onChange={(e) => setCtaLabel(e.target.value)} /></div>
              <div className="space-y-1.5"><Label>CTA URL</Label><Input value={ctaUrl} onChange={(e) => setCtaUrl(e.target.value)} placeholder="https://..." /></div>
            </div>

            <IntroRecorder
              previewUrl={introPreviewUrl}
              hasIntro={!!personalIntroVideoId}
              onRecorded={(videoId, previewUrl) => {
                setPersonalIntroVideoId(videoId);
                setIntroPreviewUrl(previewUrl);
              }}
              onClear={() => {
                setPersonalIntroVideoId(null);
                setIntroPreviewUrl(null);
              }}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Settings</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Expiry</Label>
                <Input type="datetime-local" value={expiry} onChange={(e) => setExpiry(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>No-view cadence (days)</Label>
                <Input type="number" min={1} max={30} value={noViewCadenceDays} onChange={(e) => setNoViewCadenceDays(e.target.value === "" ? "" : Number(e.target.value))} />
              </div>
            </div>
            <div className="flex flex-wrap gap-4 text-sm">
              <label
                className="flex items-center gap-2"
                title={
                  campaignRequiresNda
                    ? "Locked on: this campaign's consent tier is Named with NDA"
                    : hasNdaTierVideo
                      ? "Locked on: this playlist includes NDA-gated videos"
                      : undefined
                }
              >
                <input
                  type="checkbox"
                  checked={ndaRequired}
                  disabled={hasNdaTierVideo || campaignRequiresNda}
                  onChange={(e) => setNdaRequired(e.target.checked)}
                /> Require NDA
              </label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={allowForwarding} onChange={(e) => setAllowForwarding(e.target.checked)} /> Allow forwarding</label>
              {/* Audit V1: opt out of stats-suppression if the sender wants
                  follow-up emails to surface specific watch numbers. */}
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={suppressAnalyticsInEmail}
                  onChange={(e) => setSuppressAnalyticsInEmail(e.target.checked)}
                />
                Don't echo viewer stats in follow-up emails
              </label>
            </div>
            {hasNdaTierVideo && (
              <p className="text-xs text-gold bg-gold/10 border border-gold/30 rounded-md px-3 py-2">
                This playlist includes NDA-gated videos, so NDA signing is required —
                the recipient signs once when they open the pack, which unlocks those
                videos. (Without it they'd stay locked with no way to sign.)
              </p>
            )}
            {/* Audit V1: recipient role hint for role-scoped onboarding flows. */}
            <div className="space-y-1.5">
              <Label>Recipient role (optional)</Label>
              <Select
                value={recipientRole}
                onChange={(e) => setRecipientRole(e.target.value)}
              >
                <option value="">— skip —</option>
                {VIEWER_ROLES.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Account ref (optional)</Label><Input value={accountRef} onChange={(e) => setAccountRef(e.target.value)} placeholder="CRM account id" /></div>
              <div className="space-y-1.5"><Label>Deal ref (optional)</Label><Input value={dealRef} onChange={(e) => setDealRef(e.target.value)} placeholder="CRM deal id" /></div>
            </div>
            <div className="space-y-1.5">
              <Label>Notifiers</Label>
              <div className="border border-admin-rule rounded-md p-2 max-h-48 overflow-y-auto">
                {users?.items.map((u) => (
                  <label key={u.id} className="flex items-center gap-2 text-sm py-1">
                    <input
                      type="checkbox"
                      checked={notifierUserIds.includes(u.id)}
                      onChange={(e) =>
                        setNotifierUserIds((n) => (e.target.checked ? [...n, u.id] : n.filter((id) => id !== u.id)))
                      }
                    />
                    {u.name}
                  </label>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => nav("/admin/issuances")}>Cancel</Button>
          <Button disabled={!playlistId || recipients.some((r) => !r.name || !r.email) || send.isPending} onClick={() => send.mutate()}>
            {send.isPending ? "Sending…" : `Send to ${recipients.length} recipient${recipients.length > 1 ? "s" : ""}`}
          </Button>
        </div>
      </div>

      <div className="space-y-4">
        <Card>
          <CardHeader><CardTitle>Preview</CardTitle></CardHeader>
          <CardContent>
            <div className="bg-white text-navy rounded-md p-6 text-sm space-y-3 shadow-sm">
              <div className="font-head text-2xl font-semibold">demore</div>
              <div className="text-xs text-navy-300 tracking-wider">JUST FOR YOU</div>
              <div className="border-t border-gold/30 pt-3">
                <div className="font-head text-xl">Hi {recipients[0]?.name || "{name}"} —</div>
                <div className="text-sm text-navy-400 mt-2">{welcomeMessage || "(welcome message)"}</div>
              </div>
              {/* Mirror the real pack: the CTA only renders when BOTH a label
                  and a URL are set (see DonePage). Showing it on label alone
                  made it look like a broken button when no URL was provided. */}
              {ctaLabel && ctaUrl && (
                <a href={ctaUrl} target="_blank" rel="noreferrer" className="inline-block bg-gold text-navy font-semibold rounded-md px-4 py-2 text-sm">{ctaLabel}</a>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Audit V1: copy-link success modal after send */}
      <SentLinkDialog urls={sentUrls} onClose={() => { setSentUrls(null); nav("/admin/issuances"); }} />
    </div>
  );
}

function SentLinkDialog({ urls, onClose }: { urls: string[] | null; onClose: () => void }) {
  const open = urls !== null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {urls && urls.length === 1
              ? "Sent — here's the viewer link"
              : `Sent ${urls?.length ?? 0} packs`}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div className="space-y-2">
            {(urls ?? []).map((u, i) => (
              <div key={i} className="flex items-center gap-2 border border-admin-rule rounded-md p-2">
                <code className="flex-1 truncate text-xs">{u}</code>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    navigator.clipboard.writeText(u).then(
                      () => toast.success("Copied", { id: "copy-link" }),
                      () => toast.error("Copy blocked — select + Cmd-C"),
                    );
                  }}
                >
                  Copy link
                </Button>
              </div>
            ))}
            <p className="text-xs text-muted-foreground">
              An email is also on its way to each recipient.
            </p>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Personal intro recorder ───────────────────────────────────────────
// Records via MediaRecorder, creates a video row + Mux direct upload,
// PUTs the blob, and reports the videoId back to the parent.
function IntroRecorder({
  previewUrl,
  hasIntro,
  onRecorded,
  onClear,
}: {
  previewUrl: string | null;
  hasIntro: boolean;
  onRecorded: (videoId: string, previewUrl: string) => void;
  onClear: () => void;
}) {
  const { user } = useAuth();
  const [state, setState] = useState<"idle" | "recording" | "uploading">("idle");
  const [seconds, setSeconds] = useState(0);
  const liveVideoRef = useRef<HTMLVideoElement | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const tickRef = useRef<number | null>(null);

  async function start() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      if (liveVideoRef.current) {
        liveVideoRef.current.srcObject = stream;
        liveVideoRef.current.play().catch(() => undefined);
      }
      chunksRef.current = [];
      const mime = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus")
        ? "video/webm;codecs=vp9,opus"
        : "video/webm";
      const mr = new MediaRecorder(stream, { mimeType: mime });
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mime });
        const localUrl = URL.createObjectURL(blob);
        await handleUpload(blob, localUrl);
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setState("recording");
      setSeconds(0);
      tickRef.current = window.setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not start recording");
    }
  }

  function stop() {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    if (tickRef.current) { window.clearInterval(tickRef.current); tickRef.current = null; }
  }

  async function handleUpload(blob: Blob, localUrl: string) {
    setState("uploading");
    try {
      const senderFirst = user?.name ?? "Personal";
      // Board #2510: AEs/People Ops send packs but lack video.upload, so the
      // normal create+mux-upload path 403'd. This endpoint is authorized by
      // issuance.send and server-forces the intro video shape.
      const created = await api.post<{ id: string; uploadUrl: string }>("/api/admin/videos/personal-intro-upload", {
        title: `${senderFirst}'s intro – ${new Date().toLocaleString()}`,
        description: "Personal intro recorded for a Demo Pack",
      });
      if (!created.uploadUrl.startsWith("data:")) {
        await fetch(created.uploadUrl, { method: "PUT", body: blob });
      }
      // Auto-submit + approve isn't appropriate; intros stay draft and the
      // server still serves them as the personalIntro on the cover page
      // because the issuance references them directly (not via the playlist
      // approval gate).
      onRecorded(created.id, localUrl);
      toast.success("Intro saved");
      setState("idle");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Upload failed");
      setState("idle");
    }
  }

  useEffect(() => () => { if (tickRef.current) window.clearInterval(tickRef.current); }, []);

  return (
    <div className="border-t border-admin-rule pt-3 space-y-2">
      <div className="flex items-center justify-between">
        <Label className="flex items-center gap-2"><Video className="h-4 w-4" /> Personal intro video (optional)</Label>
        {hasIntro && (
          <Button size="sm" variant="ghost" className="text-destructive hover:bg-destructive/10" onClick={onClear}>
            <Trash2 className="h-3.5 w-3.5" /> Clear
          </Button>
        )}
      </div>
      {hasIntro && previewUrl && (
        <video src={previewUrl} controls className="w-full max-w-xs rounded-md border border-admin-rule" />
      )}
      {state === "recording" && (
        <video ref={liveVideoRef} muted className="w-full max-w-xs rounded-md border border-admin-rule bg-black" />
      )}
      <div className="flex items-center gap-2">
        {state === "idle" && !hasIntro && (
          <Button variant="outline" onClick={start}>
            <Mic className="h-4 w-4" /> Record intro
          </Button>
        )}
        {state === "recording" && (
          <>
            <Button variant="destructive" onClick={stop}>
              <CircleStop className="h-4 w-4" /> Stop
            </Button>
            <span className="text-xs text-muted-foreground">Recording — {seconds}s</span>
          </>
        )}
        {state === "uploading" && <span className="text-xs text-muted-foreground">Uploading…</span>}
      </div>
    </div>
  );
}
